import React, { Component, Fragment } from "react";
import { inject, observer } from "mobx-react";
import { withRouter, Link } from "react-router-dom";
import { Button } from "@material-ui/core";
import TableRowProfile from "../../util/TableRowProfile";
import ConfirmationModal from "../../modal/ConfirmationModal";
import image from "../../../helper/image";
import { ProfileRelationType } from "../../../types/enum";
import "./StaffActRoster.css";

@inject("venueActStore", "templateStore", "userInvitationStore", "profileStore")
@withRouter
@observer
class StaffActApplicationsTable extends Component {
    state = {
        declineConfirmationModalState: false,
        selectedApplication: null,
        applicationType: null
    };

    componentDidMount() {
        this.isMountedStaffTable = true;
    }

    componentWillUnmount() {
        this.isMountedStaffTable = false;
    }

    openDeclineModal = (application, type) => {
        this.setState({
            declineConfirmationModalState: true,
            selectedApplication: application,
            applicationType: type
        });
    };

    closeDeclineModal = () => {
        this.setState({
            declineConfirmationModalState: false,
            selectedApplication: null,
            applicationType: null
        });
    };

    handleDecline = () => {
        const { selectedApplication, applicationType } = this.state;
        if (selectedApplication) {
            const context = this.props.venueActStore.captureStaffMutation();
            if (applicationType === 'invitation') {
                return this.props.userInvitationStore.declineUserInvitation(selectedApplication.id).then(() => {
                    return this.refreshData("Application has been declined.", context);
                });
            } else {
                return this.props.venueActStore.deleteVenueFavouriteActPending(selectedApplication.id).then(() => {
                    return this.refreshData("Application has been declined.", context);
                });
            }
        }
    };

    handleAccept = (application, type) => {
        const context = this.props.venueActStore.captureStaffMutation();
        if (type === 'invitation') {
            return this.props.venueActStore.acceptInvitation(application).then(() => {
                return this.refreshData(application.invitedAct + " has been accepted to your roster.", context);
            });
        } else {
            return this.props.venueActStore.acceptVenueFavouriteActPending(application).then(() => {
                return this.refreshData("Application has been accepted.", context);
            });
        }
    };

    refreshData = (message, context) => {
        if (!context || !this.props.venueActStore.isStaffMutationCurrent(context)) return Promise.resolve();
        this.props.userInvitationStore.resetInvitations();
        return Promise.all([
            this.props.userInvitationStore.getMyUserInvitation(ProfileRelationType.VenueAct.ordinal),
            this.props.venueActStore.getStaffPendingActRoster(context.actor),
            this.props.venueActStore.getStaffActRoster(context.actor)
        ]).then(() => {
            if (!this.isMountedStaffTable || !this.props.venueActStore.isStaffMutationCurrent(context)) return;
            this.props.templateStore.openSnackbar(message);
            this.closeDeclineModal();
        });
    };

    getFilteredApplications = () => {
        const { invitations } = this.props.userInvitationStore;
        const { pendingRequest } = this.props.venueActStore;
        const { selectedVenueId } = this.props;

        const invitationApps = invitations.filter(inv => inv.additionalData).map(inv => ({
            ...inv,
            type: 'invitation'
        }));

        const pendingApps = (pendingRequest || []).map(req => ({
            ...req,
            type: 'pending'
        }));

        let allApps = [...invitationApps, ...pendingApps];

        if (selectedVenueId !== "all") {
            const selectedId = String(selectedVenueId);
            allApps = allApps.filter(app => {
                if (app.type === 'invitation') {
                    return String(app.venue?.id || '') === selectedId;
                } else {
                    const venueId = app.primaryProfile?.id || app.primaryProfile?.profileId || app.venueId;
                    return String(venueId || '') === selectedId;
                }
            });
        }

        return allApps;
    };

    render() {
        const { allowBooking } = this.props;
        const applications = this.getFilteredApplications();

        if (!applications || applications.length === 0) {
            return (
                <div className="staff-roster-empty">
                    <i className="fa fa-inbox"></i>
                    <p>No pending applications.</p>
                </div>
            );
        }

        return (
            <Fragment>
                <div className="staff-act-roster-table-container">
                    <table className="staff-pending-table">
                        <thead>
                            <tr>
                                <th style={{ width: "25%" }}>Act</th>
                                <th style={{ width: "20%" }}>Applied To Venue</th>
                                <th style={{ width: "15%" }}>Type</th>
                                <th style={{ width: "12%" }}>Status</th>
                                <th style={{ width: "28%" }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {applications.map((data, index) => {
                                const rowId = data.id || index;
                                const isInvitation = data.type === 'invitation';
                                
                                let act, venue, actType;
                                if (isInvitation) {
                                    act = data.act;
                                    venue = data.venue;
                                    actType = act?.actTypeName;
                                } else {
                                    act = data.secondaryProfile;
                                    venue = data.primaryProfile;
                                    actType = act?.actTypeName;
                                }

                                return (
                                    <tr key={rowId}>
                                        <td>
                                            <div className="staff-act-cell">
                                                <TableRowProfile
                                                    profileId={act?.id}
                                                    name={isInvitation ? data.invitedAct : (act?.fullName || act?.actName)}
                                                    avatarUrl={isInvitation ? data.actImage : image.getProfileImage(act)}
                                                />
                                                {act?.myMusician?.fullName && (
                                                    <div className="act-owner">
                                                        by {act.myMusician.fullName}
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="staff-venue-cell">
                                                <img
                                                    src={image.getProfileImage(venue)}
                                                    className="venue-avatar"
                                                    alt=""
                                                    onError={(e) => { e.target.style.display = 'none'; }}
                                                />
                                                <span className="venue-name">
                                                    {venue?.fullName || "-"}
                                                </span>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="staff-chip-group">
                                                {actType ? (
                                                    <span className="staff-chip staff-chip-type">{actType}</span>
                                                ) : (
                                                    <span className="staff-chip staff-chip-empty">-</span>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <span className="staff-status-chip pending">Pending Review</span>
                                        </td>
                                        <td>
                                            {allowBooking && (
                                                <div className="staff-action-btns">
                                                    <Button
                                                        size="small"
                                                        variant="contained"
                                                        style={{ backgroundColor: '#4caf50', color: 'white' }}
                                                        onClick={() => this.handleAccept(data, data.type)}
                                                    >
                                                        Accept
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        variant="contained"
                                                        style={{ backgroundColor: '#f44336', color: 'white' }}
                                                        onClick={() => this.openDeclineModal(data, data.type)}
                                                    >
                                                        Decline
                                                    </Button>
                                                    {act?.id && (
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            component={Link}
                                                            to={`/profile/${act.id}`}
                                                        >
                                                            View Profile
                                                        </Button>
                                                    )}
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                <ConfirmationModal
                    open={this.state.declineConfirmationModalState}
                    title="Decline Application"
                    message="Are you sure you want to decline this application?"
                    closeHandler={this.closeDeclineModal}
                    confirmationHandler={this.handleDecline}
                    declineHandler={this.closeDeclineModal}
                    confirmationLabel="Decline"
                />
            </Fragment>
        );
    }
}

export default StaffActApplicationsTable;
